import clsx from "clsx";
import { cva, VariantProps } from "cva";
import { type FocusEvent, type InputHTMLAttributes, useCallback, useId, useLayoutEffect, useRef } from "react";

import { CheckIcon } from "../Icon";
import styles from "./index.module.css";

export const variants = cva({
  variants: {
    variant: {
      fill: styles.fill,
      icon: styles.icon,
      hug: styles.hug,
    },
    align: {
      left: styles.left,
      center: styles.center,
      right: styles.right,
    },
    checked: {
      true: styles.checked,
    },
    disabled: {
      true: styles.disabled,
    },
  },
  defaultVariants: {
    variant: "fill",
    align: "left",
  },
});

export type RadioProps = InputHTMLAttributes<HTMLInputElement> & VariantProps<typeof variants>;

export const Radio = ({
  disabled,
  id,
  className,
  variant,
  align,
  checked,
  value,
  children,
  onFocus,
  onBlur,
  ...props
}: Readonly<RadioProps>) => {
  const controlId = useId();
  const state = { variant, align, checked, disabled };
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = true;
      onFocus?.(e);
    },
    [onFocus]
  );
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = false;
      onBlur?.(e);
    },
    [onBlur]
  );

  useLayoutEffect(() => {
    // key による remount で focus が失われるため、直前まで focus していた場合は復元する
    // paint 前に復元しないと focus リングのちらつきや focusout 監視との干渉が起きる
    if (isFocusedRef.current) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [checked]);

  return (
    <label className={clsx(variants(state), styles.base, className)}>
      <span className={clsx(variants(state), styles.input)}>
        <input
          ref={inputRef}
          className={clsx(variants(state), styles.nativeInput)}
          id={id ?? controlId}
          type="radio"
          disabled={disabled}
          checked={checked}
          value={value}
          onFocus={handleFocus}
          onBlur={handleBlur}
          /* React 19 は form action 完了後に form を自動リセットし、controlled radio も defaultChecked(初期表示時の選択)へ
             戻ってしまう(facebook/react#31695)。修正されるまで remount で defaultChecked を最新の選択に追随させる */
          key={String(checked)}
          {...props}
        />
        <span className={clsx(variants(state), styles.iconWrapper)}>
          <CheckIcon size="s" color="secondary" aria-hidden={true} />
        </span>
      </span>
      {!!children && <span className={clsx(variants(state), styles.label)}>{children}</span>}
    </label>
  );
};
